'use client'

import type { ChainMetadata } from '@polaris/core/models'
import { shorten } from '@polaris/core/utils'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { Check, ChevronRight, Send } from 'lucide-react'
import { motion } from 'motion/react'
import {
  type ChangeEvent,
  type MouseEventHandler,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { DestinationAddressOpenSelector } from '@/src/codegen/ampli'
import { useValidAddressInput } from '@/src/hooks/accounts/use-valid-address-input'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useSupportedChainsMap } from '@/src/queries/hooks/chains'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import {
  closeDestinationAccount,
  destinationAccountStateAtom,
  manualToWalletAddressAtom,
  selectedToChainAddressesAtom,
  toTokenAcrossChainsAtom,
  toWalletAddressAtom,
} from '@/src/stores/swap/atoms'
import { cn } from '@/src/utils/styles'
import { WalletIcon } from '../accounts/wallet-icon'
import { WatchAddressIcon } from '../accounts/watch-address'
import { BottomSheet } from '../ui/bottom-sheet'
import {
  BottomSheetListContent,
  BottomSheetListFooter,
  BottomSheetListItem,
  BottomSheetListTitle,
} from '../ui/bottom-sheet/list'
import { Button } from '../ui/button'
import { ChainImg } from '../ui/chain-img'
import {
  ListRowSide,
  ListRowSideName,
  type ListRowSideProps,
} from '../ui/list-row'
import { Textarea } from '../ui/textarea'
import { AddAnotherWalletListItem } from './add-another-wallet-list-item'

export function DestinationAccountSelect() {
  const { open, chainType } = useAtomValue(destinationAccountStateAtom)

  const [addressMenuOpen, setAddressMenuOpen] = useState(false)

  const { data: token } = useAtomValue(toTokenAcrossChainsAtom)
  const { data: chains } = useSupportedChainsMap()

  const chain = useMemo(() => {
    if (!token || !chains) return
    return chains.get(token.requested_asset.chain_id)
  }, [token, chains])

  const isOpen = open && chainType !== undefined

  const setDestinationAccountState = useSetAtom(destinationAccountStateAtom)

  const close = useCallback(() => {
    setDestinationAccountState(closeDestinationAccount())
    setAddressMenuOpen(false)
  }, [setDestinationAccountState])

  return (
    <BottomSheet
      isOpen={isOpen}
      setIsOpen={close}
      scrimClassName='bg-scrim-nonmodal'
    >
      {!addressMenuOpen ? (
        <DestinationOptions setAddressMenuOpen={setAddressMenuOpen} />
      ) : (
        <AddressMenu setAddressMenuOpen={setAddressMenuOpen} chain={chain} />
      )}
    </BottomSheet>
  )
}

function DestinationOptions({
  setAddressMenuOpen,
}: {
  setAddressMenuOpen: (open: boolean) => void
}) {
  const { chainType } = useAtomValue(destinationAccountStateAtom)
  const setDestinationAccountState = useSetAtom(destinationAccountStateAtom)
  const toWalletAddress = useAtomValue(toWalletAddressAtom)
  const { accountsWithWallets } = useAtomValue(walletAccountsAtom)
  const setToWalletAddresses = useSetAtom(selectedToChainAddressesAtom)
  const manualAddress = useAtomValue(manualToWalletAddressAtom)

  const { ampli } = useAmplitudeAnalytics({
    onMountEvent: new DestinationAddressOpenSelector(),
  })

  const accountsWithWalletsForChain = useMemo(
    () =>
      accountsWithWallets.filter((account) => account.ecosystem === chainType),
    [accountsWithWallets, chainType],
  )

  const close = () => {
    setDestinationAccountState(closeDestinationAccount())
    setAddressMenuOpen(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col gap-0 min-h-0 max-h-full'
    >
      <BottomSheetListTitle className='mb-3'>
        Select a destination
      </BottomSheetListTitle>
      <BottomSheetListContent>
        {manualAddress && toWalletAddress === manualAddress && (
          <AccountListRowButton
            onClick={() => {
              close()
            }}
            customIndicator={<Check className='text-link size-6' size={24} />}
          >
            <SendToAddressIcon />
            <ListRowSideName className='font-semibold truncate'>
              {shorten(manualAddress)}
            </ListRowSideName>
          </AccountListRowButton>
        )}
        {accountsWithWalletsForChain.map((account) => {
          const active =
            account.address === toWalletAddress &&
            toWalletAddress !== manualAddress
          return (
            <AccountListRowButton
              key={account.id ?? account.name}
              className={cn({
                'opacity-50': !('wallet' in account),
              })}
              onClick={() => {
                ampli?.destinationAddressSelectAccount()
                setToWalletAddresses([account.ecosystem, account.address])
                close()
              }}
              customIndicator={
                active ? (
                  <Check className='text-link size-6' size={24} />
                ) : (
                  <div className='size-6' />
                )
              }
            >
              {account.provider_info.img === 'no-logo' ? (
                <WatchAddressIcon className='rounded-[10px]' />
              ) : (
                <WalletIcon
                  logoUrl={account.provider_info.img}
                  walletName={account.provider_info.name}
                  size={40}
                  className={cn(
                    'rounded-[10px] overflow-hidden bg-foreground',
                    {
                      'opacity-50': !active && !('wallet' in account),
                    },
                  )}
                />
              )}
              <ListRowSideName className='font-semibold'>
                {account.name}
              </ListRowSideName>
            </AccountListRowButton>
          )
        })}
        <AddAnotherWalletListItem />
        <AccountListRowButton
          onClick={() => setAddressMenuOpen(true)}
          customIndicator={
            <ChevronRight className='text-tertiary size-6' size={24} />
          }
        >
          <SendToAddressIcon />
          <ListRowSideName className='w-full font-semibold'>
            Send to an address
          </ListRowSideName>
        </AccountListRowButton>
      </BottomSheetListContent>
      <BottomSheetListFooter setIsOpen={close} />
    </motion.div>
  )
}

function AddressMenu({
  chain,
  setAddressMenuOpen,
}: {
  chain?: ChainMetadata
  setAddressMenuOpen: (open: boolean) => void
}) {
  const [address, setAddress] = useAtom(manualToWalletAddressAtom)
  const setDestinationAccountState = useSetAtom(destinationAccountStateAtom)
  const { ampli } = useAmplitudeAnalytics()

  // Use the custom address validation hook with external state management
  const { isValid, loading, handleChange } = useValidAddressInput({
    chainType: chain?.chain_type,
    chainId: chain?.chain_id,
    managedAddress: address,
    setManagedAddress: setAddress,
  })

  const close = () => {
    if (isValid) {
      ampli?.destinationAddressAddValidManualAddress()
    }
    setDestinationAccountState(closeDestinationAccount())
    setAddressMenuOpen(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='h-full flex flex-col justify-between gap-5 px-5 pb-5'
    >
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between'>
            <span className='text-primary font-semibold'>
              Enter a destination address
            </span>
            {chain && (
              <ChainImg
                chainId={chain.chain_id}
                chainImg={chain.chain_img}
                chainName={chain.chain_name}
                size='md'
                unoptimized
              />
            )}
          </div>
          <span className='text-sm text-secondary leading-6'>
            Sending to centralized exchanges like Binance or Coinbase is not
            supported yet.
          </span>
        </div>
        <div className='flex flex-col gap-2'>
          <Textarea
            placeholder={`${chain?.chain_name} address`}
            dimension='small'
            value={address}
            onChange={(e) =>
              handleChange(e as unknown as ChangeEvent<HTMLInputElement>)
            }
            isValid={isValid}
            isInvalid={isValid === false}
            spellCheck={false}
            rows={2}
            className='text-base py-4 resize-none rounded-2xl'
          />
          <span
            className={cn('text-sm text-danger-text transition-opacity', {
              'opacity-100': isValid === false,
              'opacity-0': isValid !== false,
            })}
          >
            Invalid {chain?.chain_name} address
          </span>
        </div>
      </div>
      <div className='flex items-center gap-3'>
        <Button
          type='button'
          size='md'
          variant='secondary'
          className='w-full'
          onClick={() => setAddressMenuOpen(false)}
        >
          Back
        </Button>
        <Button
          type='button'
          size='md'
          variant='primary'
          className='w-full'
          onClick={close}
          disabled={!address || isValid === false || loading}
        >
          Done
        </Button>
      </div>
    </motion.div>
  )
}

function AccountListRowButton({
  children,
  onClick,
  customIndicator,
  containerClassName,
  className,
}: PropsWithChildren<{
  onClick: MouseEventHandler<HTMLButtonElement> | undefined
  customIndicator?: ListRowSideProps['customIndicator']
  className?: string
  containerClassName?: string
}>) {
  return (
    <BottomSheetListItem onClick={onClick}>
      <ListRowSide
        className={cn('flex-row gap-3 items-center w-full', className)}
        containerClassName={cn('w-full', containerClassName)}
        showIndicator={true}
        customIndicator={customIndicator}
      >
        {children}
      </ListRowSide>
    </BottomSheetListItem>
  )
}

export const SendToAddressIcon = ({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) => (
  <div
    className={cn(
      'flex items-center justify-center size-10 min-w-10 rounded-[10px] bg-orange-200',
      className,
    )}
  >
    <Send className='size-6 text-green-800' size={size} />
  </div>
)
